import React from 'react'
import {
  TabNav,
  TabPanel,
  PanelContainer,
  ButtonContainer,
  TabButton,
} from '~/components/shared/TabNav'
import BasicsSection from './BasicsSection'
import ServicesSection from './ServicesSection'
import CoachNotesSection from './CoachNotesSection'
import MessagesSection from './MessagesSection'
import CoachingSummarySection from './CoachingSummarySection'
import SettingsAndDocumentsSection from './SettingsAndDocumentsSection'
import CandidateActivitySection from './CandidateActivitySection'
import { CandidateProvider, useCandidate } from './CandidateProvider'
import ResumeReviewDetail from './ResumeReviewDetail'
import { cn } from '~/lib/utils'
import CandidateStatusDialog from './CandidateStatusDialog'
import ServiceRequestDetail from './ServiceRequestDetail'
import CustomizeActionPlanSection from './ActionPlan/CustomizeActionPlanSection'
import { CustomActionPlanProvider } from './ActionPlan/CustomActionPlanProvider'
import { PaginationProvider } from './ActionPlan/PaginationProvider'
import { TranslationProvider } from '../shared/TranslationProvider'
import translations from './locales.json'

const CoachCandidateViewInner = ({ urlTabParam }) => {
  const {
    detailType,
    candidate,
    candidateStatus,
    toggleShowCandidateStatusDialog,
  } = useCandidate()
  const { impersonationPath, fullName, countryLanguage, countryFlag } =
    candidate || {}

  const coachUtilizationColorMap = {
    Good: 'text-green-500',
    'Above Average': 'text-yellow-500',
    High: 'text-red-500',
  }
  const candidateStatusColorMap = {
    Paused: 'text-red-500',
    'Active Search': 'text-green-500',
    Landed: 'text-blue-500',
    Expired: 'text-gray-500',
    'Paused & Expired': 'text-red-500',
  }

  let backToServicesPath = '/'
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    params.delete('detail_type')
    params.delete('detail_item_id')
    const queryString = params.toString()
    backToServicesPath = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname
  }

  return (
    <article className="font-roboto">
      <header className="flex flex-row justify-between border-b-2 border-solid border-silver pb-6 pt-8 font-roboto">
        <div className="flex flex-col justify-between gap-2">
          <div className="mb-2 flex items-center gap-3">
            <h1 className="mb-0 font-roboto-slab font-normal text-cloud-burst">
              {fullName}
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-sm text-mine-shaft-500">
                {countryLanguage}
              </span>
              <i className={`glyphicon mb-1 bfh-flag-${countryFlag}`}></i>
            </div>
          </div>
          {impersonationPath && (
            <a
              href={impersonationPath}
              className="!underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Impersonate Candidate
            </a>
          )}
          {detailType ? (
            <a href={backToServicesPath} className="!underline">
              Back to all coaching services
            </a>
          ) : (
            <a className="!underline" href="/coach/dashboard">
              Back to Dashboard
            </a>
          )}
        </div>
        <div className="flex flex-col items-end justify-end gap-5">
          {(candidateStatus == 'Paused' ||
            candidateStatus == 'Active Search' ||
            candidateStatus == 'Paused & Expired') && (
            <div
              className={cn(
                'flex cursor-pointer items-center gap-2',
                candidateStatusColorMap[candidateStatus],
              )}
            >
              <CandidateStatusDialog />
              <div onClick={() => toggleShowCandidateStatusDialog()}>
                {candidateStatus}{' '}
                <i
                  className={cn(
                    '',
                    candidateStatus == 'Paused' ||
                      candidateStatus == 'Paused & Expired'
                      ? 'el-icon-pause-alt'
                      : 'el-icon-search-alt',
                  )}
                ></i>
              </div>
            </div>
          )}
          {candidate?.candidateStatus == 'Landed' ||
            (candidate?.candidateStatus == 'Expired' && (
              <div className="text-green-500">{candidateStatus}</div>
            ))}
          {candidate?.coachUtilization && (
            <div>
              Coach Utilization:{' '}
              <span
                className={coachUtilizationColorMap[candidate.coachUtilization]}
              >
                {candidate.coachUtilization}
              </span>
            </div>
          )}
        </div>
      </header>
      <section className="mt-10">
        <BasicsSection />
      </section>
      <section className="my-10">
        <TabGroup urlTabParam={urlTabParam} />
      </section>
    </article>
  )
}

const SwappablePanel = () => {
  const { detailType } = useCandidate()

  if (detailType) {
    switch (detailType) {
      case 'ResumeReview':
        return <ResumeReviewDetail />
      default:
        return <ServiceRequestDetail />
    }
  } else {
    return <ServicesSection />
  }
}

const TabGroup = ({ urlTabParam }) => {
  const { defaultActiveTab, handleTabChange, programLevel } = useCandidate()
  const activeTab = urlTabParam || defaultActiveTab

  return (
    <TabNav onChangeActiveTab={handleTabChange} defaultActiveTab={activeTab}>
      <ButtonContainer className="justify-start gap-4">
        <TabButton name="coach_services" text="Coach Services" />
        <TabButton name="coach_notes" text="Coach Notes" />
        <TabButton name="messages" text="Messages" />
        <TabButton name="coaching_summary" text="Coaching Summary" />
        <TabButton name="settings_and_documents" text="Settings & Documents" />
        <TabButton name="candidate_activity" text="Candidate Activity" />
        {(programLevel === 'SELECT' || programLevel === 'CONCIERGE') && (
          <TabButton
            name="customize_action_plan"
            text="Customize Action Plan"
          />
        )}
      </ButtonContainer>
      <PanelContainer className="mt-6">
        <TabPanel name="swappable_panel">
          <SwappablePanel />
        </TabPanel>
        <TabPanel name="coach_services">
          <SwappablePanel />
        </TabPanel>
        <TabPanel name="coach_notes">
          <CoachNotesSection />
        </TabPanel>
        <TabPanel name="messages">
          <MessagesSection />
        </TabPanel>
        <TabPanel name="coaching_summary">
          <CoachingSummarySection />
        </TabPanel>
        <TabPanel name="settings_and_documents">
          <SettingsAndDocumentsSection />
        </TabPanel>
        <TabPanel name="candidate_activity">
          <CandidateActivitySection />
        </TabPanel>
        <TabPanel name="customize_action_plan">
          <PaginationProvider>
            <CustomActionPlanProvider>
              <CustomizeActionPlanSection />
            </CustomActionPlanProvider>
          </PaginationProvider>
        </TabPanel>
      </PanelContainer>
    </TabNav>
  )
}

const CoachCandidateView = ({ ...props }) => {
  const { locales } = props

  let urlTabParam
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    urlTabParam = params.get('tab')
  }

  return (
    <TranslationProvider
      locale={locales.coach}
      translations={translations}
      scope={'javascript.components.CoachCandidateView'}
    >
      <CandidateProvider {...props}>
        <CoachCandidateViewInner urlTabParam={urlTabParam} />
      </CandidateProvider>
    </TranslationProvider>
  )
}

export default CoachCandidateView
